/**
 * useReservationScout — kicks the AI reservation scout and invalidates
 * the trip detail query so updated `reservationStatus` + `reservationNotes`
 * show up on the cards without a full refetch.
 *
 * Scope options:
 *   - 'trip'  (default): all food events across the trip
 *   - 'day'   + itemId:  food events on a single day
 *   - 'event' + eventId: one specific event
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { travelApi } from '../api';
import { queryKeys } from '@/lib/query-keys';
import { useTravelFeature } from './useTravelUserProfile';

export interface ScoutVars {
  scope?: 'trip' | 'day' | 'event';
  itemId?: string;
  eventId?: string;
}

export function useReservationScout(tripId: string | undefined) {
  const queryClient = useQueryClient();
  const feature = useTravelFeature('reservation_scout');

  const mutation = useMutation({
    mutationFn: (vars: ScoutVars = {}) => travelApi.runTravelReservationScout(tripId!, vars),
    onSuccess: () => {
      if (!tripId) return;
      void queryClient.invalidateQueries({ queryKey: queryKeys.travel.detail(tripId) });
    },
  });

  return {
    featureEnabled: feature.enabled,
    scout: (vars?: ScoutVars) => mutation.mutateAsync(vars ?? {}),
    isScouting: mutation.isPending,
    error: mutation.error instanceof Error ? mutation.error.message : null,
  };
}
